import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, List, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  TelegramPeerCardEntity,
  isTelegramSearchQueryValid,
  telegramDialogKey,
} from '../../../domain/entities/TelegramMessenger';

import './MessengerSearch.styles.css';

export const MESSENGER_SEARCH_DEBOUNCE_MS = 400;

interface MessengerSearchProps {
  results: TelegramPeerCardEntity[];
  isSearching: boolean;
  kindIcons: Record<TelegramPeerCardEntity['kind'], React.ReactNode>;
  onSearch: (query: string) => void;
  onClear: () => void;
  onOpen: (card: TelegramPeerCardEntity) => void;
  onJoin: (card: TelegramPeerCardEntity) => Promise<void>;
}

const MessengerSearch: React.FC<MessengerSearchProps> = ({ results, isSearching, kindIcons, onSearch, onClear, onOpen, onJoin }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [joiningKey, setJoiningKey] = useState<string>();
  const onSearchRef = useRef(onSearch);
  const onClearRef = useRef(onClear);
  onSearchRef.current = onSearch;
  onClearRef.current = onClear;

  const active = isTelegramSearchQueryValid(value);

  useEffect(() => {
    if (!isTelegramSearchQueryValid(value)) {
      onClearRef.current();
      return undefined;
    }
    const timer = window.setTimeout(() => onSearchRef.current(value), MESSENGER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value]);

  const handleJoin = async (card: TelegramPeerCardEntity) => {
    setJoiningKey(telegramDialogKey(card));
    try {
      await onJoin(card);
    } finally {
      setJoiningKey(undefined);
    }
  };

  return (
    <div className="messenger-search">
      <Input.Search
        allowClear
        value={value}
        placeholder={t('messenger-search-placeholder', { ns: 'labels' })}
        loading={isSearching}
        onChange={(e) => setValue(e.target.value)}
        onSearch={(query) => {
          if (isTelegramSearchQueryValid(query)) onSearchRef.current(query);
        }}
      />
      {active && (
        <div className="messenger-search-results">
          <Typography.Text type="secondary" className="messenger-search-section">
            {t('messenger-search-results', { ns: 'labels' })}
          </Typography.Text>
          <List
            size="small"
            loading={isSearching && results.length === 0}
            dataSource={results}
            locale={{ emptyText: t('messenger-search-empty', { ns: 'labels' }) }}
            rowKey={(card) => telegramDialogKey(card)}
            renderItem={(card) => {
              const canJoin = !card.joined && card.peerType === 'channel';
              return (
                <List.Item
                  className="messenger-search-result"
                  onClick={() => onOpen(card)}
                  actions={canJoin ? [
                    <Button
                      key="join"
                      size="small"
                      type="primary"
                      loading={joiningKey === telegramDialogKey(card)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoin(card);
                      }}
                    >
                      {t('messenger-join', { ns: 'buttons' })}
                    </Button>,
                  ] : undefined}
                >
                  <Flex vertical className="messenger-search-result-meta">
                    <Typography.Text strong ellipsis>
                      <span className="messenger-search-kind-icon" title={t(`messenger-kind-${card.kind}`, { ns: 'labels' })}>{kindIcons[card.kind]}</span>
                      {card.title}
                    </Typography.Text>
                    {card.username && <Typography.Text type="secondary" className="messenger-search-username">@{card.username}</Typography.Text>}
                  </Flex>
                </List.Item>
              );
            }}
          />
          <Typography.Text type="secondary" className="messenger-search-section">
            {t('messenger-dialogs-section', { ns: 'labels' })}
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default MessengerSearch;
